<script setup lang="ts">
import { computed } from 'vue'

import BetaRecentPostCard from '@/components/beta-dashboard/molecules/BetaRecentPostCard.vue'
import type { BetaPost } from '@/types/beta-dashboard'

const props = defineProps<{
    posts: BetaPost[]
    loading?: boolean
    error?: string | null
}>()

const emit = defineEmits<{
    browse: []
    edit: [postId: number]
    retry: []
}>()

const visiblePosts = computed(() => props.posts.slice(0, 3))
</script>

<template>
    <section class="beta-recent-posts">
        <header class="beta-recent-posts__heading">
            <div>
                <p>Conteúdo recente</p>
                <h2>Últimos posts</h2>
                <span>Acesse rapidamente as publicações atualizadas por último.</span>
            </div>

            <button type="button" @click="emit('browse')">
                Ver todas as páginas
                <span aria-hidden="true">→</span>
            </button>
        </header>

        <div v-if="loading" class="beta-recent-posts__grid" aria-label="Carregando últimos posts">
            <div
                v-for="index in 3"
                :key="index"
                class="beta-recent-posts__skeleton"
                aria-hidden="true"
            >
                <span />
                <div>
                    <i />
                    <strong />
                    <small />
                </div>
            </div>
        </div>

        <div v-else-if="error" class="beta-recent-posts__message beta-recent-posts__message--error">
            <span class="beta-recent-posts__message-icon" aria-hidden="true">!</span>
            <div>
                <strong>Não foi possível carregar os últimos posts.</strong>
                <p>{{ error }}</p>
            </div>
            <button type="button" @click="emit('retry')">Tentar novamente</button>
        </div>

        <div v-else-if="visiblePosts.length" class="beta-recent-posts__grid">
            <BetaRecentPostCard
                v-for="post in visiblePosts"
                :key="post.postId"
                :post="post"
                @edit="emit('edit', $event.postId)"
            />
        </div>

        <div v-else class="beta-recent-posts__message beta-recent-posts__message--empty">
            <span class="beta-recent-posts__message-icon" aria-hidden="true">Aa</span>
            <div>
                <strong>Nenhuma publicação por aqui ainda.</strong>
                <p>Acesse Páginas para escolher onde deseja começar.</p>
            </div>
            <button type="button" @click="emit('browse')">Ir para Páginas</button>
        </div>
    </section>
</template>

<style scoped>
.beta-recent-posts {
    display: grid;
    gap: 17px;
}

.beta-recent-posts__heading {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 20px;
}

.beta-recent-posts__heading p {
    color: var(--main-color);
    font-size: 12px;
    font-weight: 850;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-recent-posts__heading h2 {
    margin-top: 7px;
    color: #172132;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.02em;
}

.beta-recent-posts__heading div > span {
    display: block;
    margin-top: 5px;
    color: #7a899e;
    font-size: 11px;
}

.beta-recent-posts__heading > button,
.beta-recent-posts__message > button {
    display: inline-flex;
    min-height: 38px;
    flex: none;
    align-items: center;
    gap: 10px;
    border-radius: var(--beta-button-radius);
    background: color-mix(in srgb, var(--main-color) 9%, white);
    padding: 0 13px;
    color: var(--main-color);
    font-size: 12px;
    font-weight: 850;
}

.beta-recent-posts__heading > button span {
    font-size: 14px;
}

.beta-recent-posts__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
}

.beta-recent-posts__skeleton {
    overflow: hidden;
    border: 1px solid #e4e9ef;
    border-radius: 22px;
    background: #fff;
}

.beta-recent-posts__skeleton > span {
    display: block;
    height: 164px;
    background: linear-gradient(90deg, #edf1f5, #f8fafc, #edf1f5);
    background-size: 200% 100%;
    animation: beta-recent-loading 1.4s linear infinite;
}

.beta-recent-posts__skeleton > div {
    display: grid;
    gap: 10px;
    padding: 18px;
}

.beta-recent-posts__skeleton i,
.beta-recent-posts__skeleton strong,
.beta-recent-posts__skeleton small {
    display: block;
    height: 8px;
    border-radius: 999px;
    background: #edf1f5;
}

.beta-recent-posts__skeleton i {
    width: 34%;
}

.beta-recent-posts__skeleton strong {
    width: 84%;
    height: 14px;
}

.beta-recent-posts__skeleton small {
    width: 48%;
}

.beta-recent-posts__message {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    border: 1px solid #dfe7f0;
    border-radius: 20px;
    background: #fff;
    padding: 20px;
}

.beta-recent-posts__message-icon {
    display: grid;
    width: 42px;
    height: 42px;
    place-items: center;
    border-radius: 14px;
    background: color-mix(in srgb, var(--main-color) 10%, white);
    color: var(--main-color);
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 16px;
    font-weight: 700;
}

.beta-recent-posts__message strong {
    color: #1a304e;
    font-size: 14px;
    font-weight: 850;
}

.beta-recent-posts__message p {
    margin-top: 4px;
    color: #77879d;
    font-size: 11px;
    line-height: 1.5;
}

.beta-recent-posts__message--error {
    border-color: #f0cccc;
    background: #fff7f7;
}

.beta-recent-posts__message--error .beta-recent-posts__message-icon {
    background: #fbe7e7;
    color: #984d4d;
}

.beta-recent-posts__message--error strong,
.beta-recent-posts__message--error p {
    color: #984d4d;
}

.beta-recent-posts__message--error > button {
    background: #984d4d;
    color: #fff;
}

@keyframes beta-recent-loading {
    to { background-position: -200% 0; }
}

@media (max-width: 900px) {
    .beta-recent-posts__grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 640px) {
    .beta-recent-posts__heading {
        align-items: flex-start;
        gap: 12px;
    }

    .beta-recent-posts__heading > div {
        min-width: 0;
    }

    .beta-recent-posts__heading > button {
        justify-content: center;
        align-self: flex-start;
        white-space: nowrap;
    }

    .beta-recent-posts__grid {
        grid-template-columns: 1fr;
    }

    .beta-recent-posts__skeleton {
        display: grid;
        grid-template-columns: 112px minmax(0, 1fr);
    }

    .beta-recent-posts__skeleton > span {
        height: 132px;
    }

    .beta-recent-posts__message {
        grid-template-columns: auto minmax(0, 1fr);
    }

    .beta-recent-posts__message > button {
        grid-column: 1 / -1;
        justify-content: center;
    }
}
</style>
